import React, { useState, useRef, useEffect } from 'react';
import { EtlSection, EtlGrid, EtlField, EtlReadonly, ChoiceCards, DropZone, EtlBanner } from '../EtlPages';
import PDFPageSelector from './PDFPageSelector';
import ExcelSheetSelector from './ExcelSheetSelector';

const FileUploadComponent = ({ onFileUpload, isLoading, error, onBack, fileType, hideFooter = false, onFooterChange }) => {
  const [selectedFile, setSelectedFile] = useState(null);
  const handleUploadRef = useRef(() => {});
  const clearSelectionRef = useRef(() => {});
  const [extractionMethod, setExtractionMethod] = useState('aiExtraction'); // 'aiExtraction', 'formRecognizer', 'textract'
  const [preprocessPdf, setPreprocessPdf] = useState(false);
  const [rejected, setRejected] = useState(null);
  const [selectedPages, setSelectedPages] = useState(null);
  const [selectedSheets, setSelectedSheets] = useState(null);
  const fileInputRef = useRef(null);
  const actionLabel = extractionMethod === 'aiExtraction' ? 'Configure AI extraction'
    : extractionMethod === 'textract' ? 'Extract tables with AWS Textract'
    : extractionMethod === 'formRecognizer' ? 'Extract with Azure Form Recognizer'
    : 'Read file for manual mapping';

  const fileTypes = [
    { value: 'audit', label: 'Daily Audit Report' },
    { value: 'occupancy', label: 'Occupancy Report' },
    { value: 'revenue', label: 'Revenue Report' },
    { value: 'financial', label: 'Financial Statement' },
    { value: 'custom', label: 'Custom Report Type' }
  ];

  /**
   * Accept a file from the kit DropZone, which owns the drag state and the
   * hidden input. Unsupported extensions are refused here rather than silently
   * ignored — the old drop handler dropped them on the floor with no feedback.
   */
  const handleFileDrop = (file) => {
    if (!file) return;
    if (!isValidFileType(file.name)) {
      setRejected(file.name);
      return;
    }
    setRejected(null);
    setSelectedFile(file);
    setExtractionMethod('aiExtraction');
    setSelectedPages(null);
    setSelectedSheets(null);
  };

  const isValidFileType = (fileName) => {
    const allowedExtensions = ['.pdf', '.txt', '.xlsx', '.xls', '.xlsm', '.csv', '.xml'];
    const extension = fileName.toLowerCase().substring(fileName.lastIndexOf('.'));
    return allowedExtensions.includes(extension);
  };

  /**
   * Publish this step's primary action to the wizard frame.
   *
   * The frame owns the one footer every step shares, so the step reports what
   * its Continue button should do rather than drawing its own. Kept in an
   * effect (not inline) so the frame re-reads it when the selection, the
   * extraction method or the loading state changes.
   */
  useEffect(() => {
    if (!onFooterChange) return;
    onFooterChange({
      next: {
        label: actionLabel,
        onClick: () => handleUploadRef.current(),
        disabled: !selectedFile || !fileType,
        busy: isLoading,
        busyLabel: 'Processing…',
      },
      secondary: selectedFile ? (
        <button type="button" className="cp-btn cp-ghost" onClick={() => clearSelectionRef.current()} disabled={isLoading}>
          Clear
        </button>
      ) : null,
      note: !fileType
        ? 'Pick a file type on the previous step first.'
        : !selectedFile ? 'Choose a sample file to continue.' : null,
    });
  }, [onFooterChange, selectedFile, fileType, isLoading, actionLabel]);

  const handleUpload = () => {
    if (selectedFile && fileType) {
      const isSheet = /\.(xlsx|xls|xlsm|csv)$/i.test(selectedFile.name);
      const isPdf = /\.pdf$/i.test(selectedFile.name);
      onFileUpload(
        selectedFile, fileType, extractionMethod,
        isSheet ? selectedSheets : isPdf ? selectedPages : null,
        isPdf && extractionMethod !== 'aiExtraction' ? preprocessPdf : false
      );
    }
  };

  handleUploadRef.current = handleUpload;

  const clearSelection = () => {
    setSelectedFile(null);
    setExtractionMethod('aiExtraction');
    setPreprocessPdf(false);
    setSelectedPages(null);
    setSelectedSheets(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };
  clearSelectionRef.current = clearSelection;

  const isPdf = !!selectedFile && selectedFile.name.toLowerCase().endsWith('.pdf');
  const isSheet = !!selectedFile && /\.(xlsx|xls|xlsm|csv)$/i.test(selectedFile.name);
  // CSV / XML / Excel are already structured: the alternative to the model is
  // parsing the rows into a table and mapping columns by hand, not OCR. The
  // picker used to offer Form Recognizer / Textract here, so nobody chose them
  // for a CSV and the file never reached Field mapping.
  const isParsable = !!selectedFile && /\.(csv|xml|xlsx|xls|xlsm)$/i.test(selectedFile.name);
  const isText = !!selectedFile && /\.txt$/i.test(selectedFile.name);
  const showMethod = !!selectedFile;
  const methodOptions = isParsable || isText
    ? [
        {
          value: 'aiExtraction',
          title: 'AI extraction',
          sub: 'The model reads the rows as text and returns database-ready data. Best when the layout varies or needs interpretation.',
          disabled: isLoading,
        },
        {
          value: isText ? 'text-parser' : 'excel-parser',
          title: 'Parse and map by hand',
          sub: 'Reads the file into a table exactly as-is; you point each column at a destination field on the next steps. Best for fixed-layout exports.',
          disabled: isLoading,
        },
      ]
    : [
        {
          value: 'aiExtraction',
          title: 'AI extraction',
          sub: 'The model analyses the document visually. Fast and accurate, about $0.02 a page.',
          disabled: isLoading,
        },
        {
          value: 'formRecognizer',
          title: 'Azure Form Recognizer',
          sub: 'OCR. Good for structured forms with a consistent layout.',
          disabled: isLoading,
        },
        {
          value: 'textract',
          title: 'AWS Textract',
          sub: 'OCR. An alternative for documents with complex tables.',
          disabled: isLoading,
        },
      ];

  return (
    <>
      <EtlSection title="Feed">
        <EtlGrid>
          <EtlField label="Report type" help="Chosen on the previous step.">
            <EtlReadonly>{fileTypes.find((t) => t.value === fileType)?.label || fileType || 'Not set'}</EtlReadonly>
          </EtlField>
        </EtlGrid>
      </EtlSection>

      <EtlSection
        title="Sample file"
        description="One real file, up to 50MB. PDF, TXT, CSV, XML or Excel."
      >
        {selectedFile ? (
          <div className="ep-panel" style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
            <span className="cp-name">{selectedFile.name}</span>
            <span className="cp-code">{(selectedFile.size / 1024 / 1024).toFixed(2)} MB</span>
            <div style={{ flex: 1 }} />
            <button type="button" className="cp-btn cp-sm cp-ghost" onClick={clearSelection} disabled={isLoading}>
              Choose a different file
            </button>
          </div>
        ) : (
          <DropZone
            accept=".pdf,.txt,.xlsx,.xls,.xlsm,.csv,.xml"
            disabled={isLoading}
            hint="PDF · TXT · CSV · XML · XLSX · XLS · XLSM"
            onFiles={(files) => handleFileDrop(files[0])}
          />
        )}
      </EtlSection>

      {showMethod ? (
        <EtlSection
          title="How to read it"
          description={isParsable || isText
            ? 'Choose AI extraction or read the file for manual mapping without AI.'
            : 'Choose AI extraction or an OCR service. OCR runs when you click Extract and returns tables and text for manual mapping.'}
        >
          <ChoiceCards
            ariaLabel="Extraction method"
            value={extractionMethod}
            onChange={setExtractionMethod}
            options={methodOptions}
          />

          {isPdf && extractionMethod !== 'aiExtraction' ? (
            <label style={{ display: 'inline-flex', alignItems: 'flex-start', gap: 8, marginTop: 12, fontSize: 13, color: 'var(--lodge-onyx-700)' }}>
              <input
                type="checkbox"
                checked={preprocessPdf}
                onChange={(e) => setPreprocessPdf(e.target.checked)}
                disabled={isLoading}
                style={{ marginTop: 2 }}
              />
              <span>
                Preprocess the PDF for OCR
                <span className="cp-help" style={{ display: 'block' }}>
                  Re-renders at 300 DPI and enhances contrast and sharpness. Slower, but rescues a poor scan.
                </span>
              </span>
            </label>
          ) : null}
        </EtlSection>
      ) : null}

      {isPdf ? (
        <EtlSection title="Pages to read" description="Leave everything selected to read the whole document.">
          <PDFPageSelector
            file={selectedFile}
            onPageSelection={setSelectedPages}
            initialSelectedPages={selectedPages}
          />
        </EtlSection>
      ) : null}

      {isSheet ? (
        <EtlSection title="Sheet to read">
          <ExcelSheetSelector
            file={selectedFile}
            onSheetSelection={setSelectedSheets}
            initialSelectedSheet={selectedSheets}
          />
        </EtlSection>
      ) : null}

      {rejected ? (
        <div style={{ marginTop: 16 }}>
          <EtlBanner tone="warn" title="Unsupported file">
            {rejected} is not a format this step can read. Use PDF, TXT, CSV, XML or Excel.
          </EtlBanner>
        </div>
      ) : null}

      {error ? (
        <div style={{ marginTop: 16 }}>
          <EtlBanner tone="error" title="Could not process the file">{error}</EtlBanner>
        </div>
      ) : null}

      {/* Navigation lives in the wizard frame; this stays only for any caller
          that still renders the component standalone. */}
      <div className={`justify-between ${hideFooter ? 'hidden' : 'flex'}`} style={{ marginTop: 16, gap: 8 }}>
        {onBack ? <button type="button" className="cp-btn" onClick={onBack} disabled={isLoading}>Back</button> : null}
        <div style={{ display: 'flex', gap: 8 }}>
          <button type="button" className="cp-btn cp-primary" onClick={handleUpload} disabled={!selectedFile || !fileType || isLoading}>
            {isLoading ? 'Processing…' : actionLabel}
          </button>
          <button type="button" className="cp-btn cp-ghost" onClick={clearSelection} disabled={isLoading}>Clear</button>
        </div>
      </div>

      <EtlSection title="What happens next">
        <div className="ep-panel">
          <ul style={{ margin: 0, paddingLeft: 18, fontSize: 12.5, lineHeight: 1.6, color: 'var(--lodge-onyx-600)' }}>
            {extractionMethod === 'aiExtraction' ? (
              <>
                <li>Next, choose your AI settings and instructions, then click Extract with AI to run extraction.</li>
                <li>You review what it found, with confidence scores, and accept or correct it.</li>
                <li>Confirmed output is database-ready, so the manual field-mapping step is skipped.</li>
              </>
            ) : (
              <>
                {['excel-parser', 'text-parser'].includes(extractionMethod) ? (
                  <li>The file is read for manual mapping without running AI extraction.</li>
                ) : (
                  <li>{extractionMethod === 'formRecognizer' ? 'Azure Form Recognizer' : 'AWS Textract'} transcribes tables, key–value pairs and text.</li>
                )}
                <li>You preview what was read, then map its columns to destination fields by hand.</li>
              </>
            )}
            <li>Nothing is imported here — this only teaches the pipeline the file's shape.</li>
          </ul>
        </div>
      </EtlSection>
    </>
  );
};

export default FileUploadComponent;
